import { motion } from 'motion/react';
import React from 'react';

import {
  CircularProgressStyles,
  ProgressTextStyles,
} from './CircularProgress.styles';

interface CircularProgressProps {
  progress: number;
  startProgress?: number;
  size?: number;
  strokeWidth?: number;
  showPercentage?: boolean;
  className?: string;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  startProgress = 0,
  size = 282,
  strokeWidth = 12,
  showPercentage = true,
  className,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const initialStrokeDashoffset =
    circumference - (startProgress / 100) * circumference;

  const [displayProgress, setDisplayProgress] = React.useState(startProgress);

  React.useEffect(() => {
    const start = startProgress;
    const end = progress;
    const duration = 1500;

    let startTime: number;
    let animationId: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const currentValue = start + (end - start) * progress;
      setDisplayProgress(currentValue);

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [startProgress, progress]);

  return (
    <CircularProgressStyles size={size} className={className}>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${size} ${size}`}
        style={{ maxWidth: size, maxHeight: size }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={strokeWidth}
          fill="none"
        />

        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#803EFF"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          initial={{ strokeDashoffset: initialStrokeDashoffset }}
          animate={{ strokeDashoffset }}
          transition={{
            duration: 1.5,
            ease: 'easeOut',
            delay: 0,
          }}
          style={{
            transformOrigin: 'center',
            transform: 'rotate(-90deg)',
            filter: 'drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))',
          }}
        />
      </svg>
      {showPercentage && (
        <ProgressTextStyles>
          <motion.span>
            <motion.span>{Math.round(displayProgress)}%</motion.span>
          </motion.span>
        </ProgressTextStyles>
      )}
    </CircularProgressStyles>
  );
};
